import React from "react";
import { waLink } from "@/lib/brand";
import Mnemonic from "@/components/Mnemonic";

const COTTAGES = [
  {
    id: "twin-suites",
    name: "Twin Suites",
    kind: "In the front · paired rooms",
    body:
      "The rooms that sit at the front of the property. Utilitarian, straightforward, unfussy. A bed, a window that opens, a veranda for the morning. What is here is what is needed, and nothing that is not.",
    character: "Utilitarian · straightforward",
    orientation: "Front of the property · paired plan",
    tone: "cream",
    ask: "Hello FarmStory, I'd like to ask about the Twin Suites.",
  },
  {
    id: "jungle-villas",
    name: "Jungle Villas",
    kind: "Raised deck · across the gawa",
    body:
      "Raised on a deck across the gawa, the villas sit inside a small canopy of golpata and other native trees. More spacious than the front rooms, and quieter — the water is close on one side, the trees on the other. Meant to be inhabited slowly, over more than one day.",
    character: "Spacious · intimate",
    orientation: "Across the gawa · raised deck · native canopy",
    tone: "dark",
    ask: "Hello FarmStory, I'd like to ask about the Jungle Villas.",
  },
];

const toneClass = (t) =>
  t === "cream" ? "block-placeholder--cream" : t === "clay" ? "block-placeholder--clay" : "block-placeholder--dark";

export default function Stay() {
  return (
    <div data-testid="page-stay">
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-14">
          <Mnemonic size={44} className="mb-8" style={{ opacity: 0.9 }} />
          <div
            className="italic-display mb-3"
            style={{ fontSize: "clamp(18px, 2vw, 24px)", color: "var(--sundari-gold)", opacity: 0.9 }}
            data-testid="page-bengali"
          >
            থাকা
          </div>
          <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Stay</div>
          <h1 className="italic-display mt-6" style={{ fontSize: "clamp(48px, 7.5vw, 104px)", lineHeight: 1 }}>
            Two ways
            <br />
            <span style={{ color: "var(--sundari-gold)" }}>to be here.</span>
          </h1>
          <p className="mt-8 max-w-[720px]" style={{ fontSize: "17px", lineHeight: 1.75 }}>
            The Twin Suites at the front, and the Jungle Villas raised on a deck across the gawa,
            wrapped in golpata and the other native trees of the delta. One is utilitarian, one is
            spacious. Both belong here.
          </p>
        </div>
      </section>

      <section style={{ background: "var(--bamboo-cream)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {COTTAGES.map((c) => (
              <article
                key={c.id}
                className="hair-all rounded-[8px] overflow-hidden flex flex-col"
                style={{ background: "var(--salt-white)" }}
                data-testid={`cottage-${c.id}`}
              >
                <div className={`block-placeholder ${toneClass(c.tone)}`} style={{ height: 320 }} />
                <div className="p-7 md:p-9 flex flex-col grow">
                  <div className="label-track" style={{ color: "var(--delta-clay)" }}>
                    {c.kind}
                  </div>
                  <h2 className="italic-display mt-3" style={{ fontSize: "40px", lineHeight: 1.02 }}>
                    {c.name}
                  </h2>
                  <p className="mt-5" style={{ fontSize: "15px", lineHeight: 1.75 }}>
                    {c.body}
                  </p>

                  <dl className="mt-7 space-y-3 hair-t pt-5 text-[13px]">
                    <div className="flex gap-3">
                      <dt className="label-track shrink-0" style={{ color: "var(--golpata-green)", minWidth: 110 }}>
                        Character
                      </dt>
                      <dd>{c.character}</dd>
                    </div>
                    <div className="flex gap-3">
                      <dt className="label-track shrink-0" style={{ color: "var(--golpata-green)", minWidth: 110 }}>
                        Positioning
                      </dt>
                      <dd>{c.orientation}</dd>
                    </div>
                  </dl>

                  <div className="mt-8">
                    <a
                      href={waLink(c.ask)}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-pill btn-pill--dark"
                      data-testid={`cottage-cta-${c.id}`}
                    >
                      Ask About This
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}>
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <blockquote
            className="italic-display"
            style={{ fontSize: "clamp(24px, 3vw, 34px)", lineHeight: 1.35 }}
          >
            &ldquo;Every cottage stands lightly on the earth.&rdquo;
          </blockquote>
        </div>
      </section>
    </div>
  );
}
